<template>
  <div>
    <div class="space-y-16">
      <div class="text-static-default-hi heading-3">
        {{ i18n(translations.title) }}
      </div>
      <RevInfoBlock
        v-if="state === 'ON_HOLD'"
        :content="i18n(translations.paymentFailedInfoContent)"
        :icon="IconWarning"
        :title="i18n(translations.paymentFailedInfoTitle)"
        variant="danger"
      />
      <RevInfoBlock
        v-if="failedPaymentDuringAttemptPeriod"
        :content="
          i18n(translations.failedPaymentDuringAttemptPeriodInfoContent)
        "
        :icon="IconWarning"
        :title="i18n(translations.failedPaymentDuringAttemptPeriodInfoTitle)"
        variant="danger"
      />
      <div v-if="dueAmount">
        <div class="text-static-default-hi body-2">
          {{ i18n(translations.totalDue) }}
        </div>
        <div class="text-static-default-hi punchline">
          {{ i18n.price(dueAmount) }}
        </div>
      </div>
      <div v-if="nextPayment">
        <div class="text-static-default-hi body-2">
          {{ i18n(translations.nextPayment) }}
        </div>
        <div class="flex items-baseline gap-4">
          <div class="text-static-default-hi heading-2">
            {{ i18n.price(nextPayment.price) }}
          </div>
          <div class="text-static-default-low body-2">
            {{
              i18n(translations.nextPaymentDate, {
                date: i18n.date(
                  new Date(nextPayment.date),
                  INSURANCE_DATE_FORMAT,
                ),
              })
            }}
          </div>
        </div>
      </div>
    </div>

    <RevButton
      v-if="updatePaymentMethodButton"
      class="mt-16 md:mt-24"
      full-width="always"
      :tracking="{
        zone: TRACKING_ZONES.POLICY_DETAILS,
        name: 'update-payment-method',
        hasDueAmount: dueAmount !== null,
      }"
      :variant="updatePaymentMethodButton.variant"
      @click="goToUpdatePaymentMethodPage"
    >
      {{ updatePaymentMethodButton.label }}
    </RevButton>
  </div>
</template>

<script setup lang="ts">
import { navigateTo } from '#imports'
import { computed } from 'vue'

import type { Price } from '@backmarket/http-api/src/api-models'
import { PaymentFlow } from '@backmarket/nuxt-layer-payment/pages/config/flows'
import { usePaymentRouteLocation } from '@backmarket/nuxt-layer-payment/pages/navigation/usePaymentRouteLocation'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButton } from '@ds/components/Button'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { IconWarning } from '@ds/icons/IconWarning'

import {
  INSURANCE_DATE_FORMAT,
  TRACKING_ZONES,
} from '~/scopes/dashboard/services-hub/constant'
import type { PolicyDetails } from '~/scopes/dashboard/services-hub/types'

import translations from './PaymentDetails.translations'

const i18n = useI18n()

const props = defineProps<{
  dueAmount: Price | null
  nextPayment: {
    date: string
    price: Price
  } | null
  state: PolicyDetails['policy']['state']
  policyId: string
  isMonthly: boolean
}>()

// Before the status is changed to ON_HOLD, we try to capture the payment 3 times
const failedPaymentDuringAttemptPeriod = computed(() => {
  return props.state === 'ACTIVE' && props.dueAmount
})

const updatePaymentMethodButton = computed(() => {
  if (failedPaymentDuringAttemptPeriod.value || props.state === 'ON_HOLD') {
    return {
      label: i18n(translations.catchUpPaymentButton),
      variant: 'primary' as const,
    }
  }

  if (props.state === 'ACTIVE' && !props.dueAmount && props.isMonthly) {
    return {
      label: i18n(translations.updatePaymentMethodButton),
      variant: 'secondary' as const,
    }
  }

  return false
})

const getPaymentRouteLocation = usePaymentRouteLocation(
  PaymentFlow.INSURANCE_UPDATE_PAYMENT_METHOD,
)

function goToUpdatePaymentMethodPage() {
  const paymentRouteLocation = getPaymentRouteLocation({
    policyId: props.policyId,
  })

  navigateTo(paymentRouteLocation, { external: true })
}
</script>
